import { Button, Input, Rate, Space, Table, Tag } from "antd";
import { EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { loadAllStaff } from "../../redux/rtk/features/user/userSlice";
import ColVisibilityDropdown from "../Shared/ColVisibilityDropdown";
import ViewBtn from "../Buttons/ViewBtn";
import BtnViewSvg from "../UI/Button/btnViewSvg";

function GetAllEmployeeSkills({ data = [], onEdit, onRefresh }) {
	const dispatch = useDispatch();
	const [columnsToShow, setColumnsToShow] = useState([]);
	const [searchEmployee, setSearchEmployee] = useState("");
	const [searchSkill, setSearchSkill] = useState("");
	const [filterCategory, setFilterCategory] = useState("");
	const [filterLevel, setFilterLevel] = useState("");
	const staffList = useSelector((state) => state.users?.list || []);

	useEffect(() => {
		dispatch(loadAllStaff({ status: true }));
	}, [dispatch]);

	const mapEmployeeName = (employeeId) => {
		const employee = staffList.find((e) => e.id === employeeId);
		if (employee) {
			return (
				employee.EmployeeName ||
				`${employee.firstName || ""} ${employee.lastName || ""}`.trim() ||
				employee.UserName
			);
		}
		return employeeId;
	};

	const getProficiencyColor = (level) => {
		switch (level) {
			case "Expert":
				return "green";
			case "Advanced":
				return "blue";
			case "Intermediate":
				return "orange";
			default:
				return "default";
		}
	};

	const getStatusColor = (status) => {
		switch (status) {
			case "Active":
				return "green";
			case "In Progress":
				return "blue";
			case "Completed":
				return "purple";
			case "Expired":
				return "red";
			default:
				return "default";
		}
	};

	const columns = [
		{
			id: 1,
			title: "S.No",
			dataIndex: "serial",
			key: "serial",
		},
		{
			title: "Employee",
			dataIndex: "employeeName",
			key: "employeeName",
		},
		{
			title: "Skill Category",
			dataIndex: "skillCategory",
			key: "skillCategory",
		},
		{
			title: "Skill Name",
			dataIndex: "skillName",
			key: "skillName",
		},
		{
			title: "Proficiency Level",
			dataIndex: "proficiencyLevel",
			key: "proficiencyLevel",
			render: (level) => (
				<Tag color={getProficiencyColor(level)}>
					{level || "Beginner"}
				</Tag>
			),
		},
		{
			title: "Current Rating",
			dataIndex: "currentRating",
			key: "currentRating",
			render: (rating) => (
				<Rate disabled defaultValue={rating} allowHalf style={{ fontSize: 14 }} />
			),
		},
		{
			title: "Target Rating",
			dataIndex: "targetRating",
			key: "targetRating",
			render: (rating) => (rating ? <Rate disabled defaultValue={rating} allowHalf style={{ fontSize: 14 }} /> : "-"),
		},
		{
			title: "Experience",
			dataIndex: "experienceYears",
			key: "experienceYears",
			render: (years) => (years ? `${years} years` : "-"),
		},
		{
			title: "Assessment Date",
			dataIndex: "assessmentDate",
			key: "assessmentDate",
			render: (date) => (date ? dayjs(date).format("DD-MM-YYYY") : "-"),
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			render: (status) => (
				<Tag color={getStatusColor(status)}>
					{status || "Active"}
				</Tag>
			),
		},
		{
			title: "Actions",
			key: "action",
			render: (_, record) => {
				const id = record.id;
				return (
					<Space>
						<ViewBtn
							path={`/admin/employeeSkill/${id}`}
							text="View"
							icon={<BtnViewSvg />}
						/>
						<Button
							type="link"
							icon={<EditOutlined />}
							onClick={() => onEdit && onEdit(record)}
						/>
					</Space>
				);
			},
		},
	];

	useEffect(() => {
		setColumnsToShow(columns);
	}, []);

	const columnsToShowHandler = (val) => {
		setColumnsToShow(val);
	};

	const addKeys = (arr) =>
		(Array.isArray(arr) ? arr : []).map((i, index) => ({
			...i,
			key: i.id || index,
			serial: index + 1,
			employeeName: i.employeeName || mapEmployeeName(i.employeeId),
		}));

	const filteredData = addKeys(data).filter((item) => {
		const employeeName = (item.employeeName || "").toLowerCase();
		const skillName = (item.skillName || "").toLowerCase();
		const matchesEmployee = employeeName.includes(searchEmployee.toLowerCase());
		const matchesSkill = skillName.includes(searchSkill.toLowerCase());
		const matchesCategory = !filterCategory || item.skillCategory === filterCategory;
		const matchesLevel = !filterLevel || item.proficiencyLevel === filterLevel;
		return matchesEmployee && matchesSkill && matchesCategory && matchesLevel;
	});

	const uniqueCategories = [...new Set(data.map((item) => item.skillCategory))].filter(Boolean);
	const uniqueLevels = [...new Set(data.map((item) => item.proficiencyLevel))].filter(Boolean);

	return (
		<div className="employee-skills-table-container">
			<div className="text-center my-2 flex justify-between items-center">
				<h5 className="text-color-2 text-xl mb-2">Employee Skills List</h5>
				{data && data.length > 0 && (
					<div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
						<ColVisibilityDropdown
							options={columns}
							columns={columns}
							columnsToShowHandler={columnsToShowHandler}
						/>
						<Input
							placeholder="Search by Employee"
							value={searchEmployee}
							onChange={(e) => setSearchEmployee(e.target.value)}
							style={{ width: 150 }}
						/>
						<Input
							placeholder="Search by Skill"
							value={searchSkill}
							onChange={(e) => setSearchSkill(e.target.value)}
							style={{ width: 150 }}
						/>
						<Input
							placeholder="Filter by Category"
							value={filterCategory}
							onChange={(e) => setFilterCategory(e.target.value)}
							style={{ width: 150 }}
						/>
						<Input
							placeholder="Filter by Level"
							value={filterLevel}
							onChange={(e) => setFilterLevel(e.target.value)}
							style={{ width: 150 }}
						/>
					</div>
				)}
			</div>

			<Table
				scroll={{ x: true, y: "calc(100vh - 350px)" }}
				loading={false}
				columns={columnsToShow}
				dataSource={filteredData}
				pagination={{
					pageSize: 10,
					showSizeChanger: true,
					showTotal: (total, range) =>
						`${range[0]}-${range[1]} of ${total} items`,
					pageSizeOptions: ["10", "20", "50", "100"],
				}}
			/>
		</div>
	);
}

export default GetAllEmployeeSkills;

